import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { Icon } from "../icons";
import { COLORS, TYPOGRAPHY } from "../constants/color";
import profilePhoto from "../assets/icons/watermark.png";
import axios from "axios";
import { url } from "../constants/url";
import { useAuth } from "../context/authContext";

const statusStyles = {
  APPROVED: { bgcolor: "#8CD47E", color: "#ffffff" },
  PENDING: { bgcolor: "#F8D66D", color: "#ffffff" },
  REJECTED: { bgcolor: "#FF6961", color: "#ffffff" },
};

// Function to format status display
const getStatusText = (status) => {
  switch (status) {
    case "APPROVED":
      return "Active";
    case "PENDING":
      return "Pending";
    case "REJECTED":
      return "Rejected";
    default:
      return status || "Pending";
  }
};

// Function to format role display
const getRoleDisplay = (role) => {
  const roleMap = {
    CLIENT: "Client",
    ENGINEER: "Engineer",
    ADMIN: "Administrator",
    SUPERADMIN: "Super Admin",
  };
  return roleMap[role] || role || "User";
};

const UserProfileSidebar = ({ open, onClose, user }) => {
  const { token } = useAuth();
  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState(user);
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateError, setUpdateError] = useState("");

  // Fetch fresh user data when sidebar opens
  useEffect(() => {
    if (open && user?._id && token) {
      fetchUserData();
    }
  }, [open, user?._id, token]);

  // Update editData when userData changes
  useEffect(() => {
    if (userData) {
      setEditData(userData);
      setUpdateMessage("");
      setUpdateError("");
    }
  }, [userData]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          userID: user._id,
        },
      });

      // Extract the first user from the response
      if (response.data?.users && response.data.users.length > 0) {
        setUserData(response.data.users[0]);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      // Keep using the passed user data if fetch fails
      setUserData(user);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setUpdating(true);
      setUpdateError("");
      setUpdateMessage("");

      const displayUser = userData || user;

      // Prepare update payload
      const updatePayload = {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
      };

      // Add role-specific fields
      if (displayUser.role === "CLIENT") {
        updatePayload.companyName = editData.companyName;
        updatePayload.companyAddress = editData.companyAddress;
      } else if (displayUser.role === "ENGINEER" || displayUser.role === "ADMIN" || displayUser.role === "SUPERADMIN") {
        updatePayload.detailedAddress = editData.detailedAddress;
        updatePayload.city = editData.city;
        updatePayload.state = editData.state;
      }

      const response = await axios.put(
        `${url}/api/users/update/${displayUser._id}`,
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.user) {
        setUserData(response.data.user);
        setEditData(response.data.user);
        setIsEditMode(false);
        setUpdateMessage("Profile updated successfully!");
        setTimeout(() => setUpdateMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setUpdateError(
        error.response?.data?.message || "Failed to update profile"
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditData(userData);
    setIsEditMode(false);
    setUpdateError("");
    setUpdateMessage("");
  };

  if (!userData) return null;

  const displayUser = userData || user;
  const displayStatus = getStatusText(displayUser.status);
  const displayRole = getRoleDisplay(displayUser.role);
  const joinedDate = displayUser.joinedAt
    ? new Date(displayUser.joinedAt).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')
    : "N/A";

  return (
    <>
      {/* Overlay */}
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1299,
          }}
        />
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: "100%", sm: 400 },
          maxWidth: "100%",
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            alignItems: "center",
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Profile
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {!isEditMode && !loading && (
              <IconButton
                onClick={() => setIsEditMode(true)}
                sx={{ p: 0.5 }}
                title="Edit profile"
              >
                <Icon name="edit" color={COLORS.PRIMARY} size={20} />
              </IconButton>
            )}
            <IconButton onClick={onClose} sx={{ p: 0.5 }}>
              <Icon name="close2" color={COLORS.PRIMARY} size={20} />
            </IconButton>
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Alert Messages */}
        {updateMessage && !isEditMode && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {updateMessage}
          </Alert>
        )}
        {updateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {updateError}
          </Alert>
        )}

        {/* Profile Content */}
        {!loading && (
          <>
            {/* Profile Header Section */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Avatar
                src={profilePhoto}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: COLORS.PRIMARY,
                  fontSize: 32,
                  fontWeight: 600,
                  margin: "0 auto",
                  mb: 2,
                }}
              >
                {displayUser.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              {isEditMode ? (
                <TextField
                  fullWidth
                  label="Name"
                  value={editData.name || ""}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  size="small"
                  sx={{ mb: 1, mt: 1 }}
                />
              ) : (
                <>
                  <Typography
                    sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 0.5 }}
                  >
                    {displayUser.name || "User"}
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.MUTED_TEXT,
                      mb: 1.5,
                    }}
                  >
                    {displayRole}
                  </Typography>
                  <Chip
                    label={displayStatus}
                    size="small"
                    sx={{
                      ...(statusStyles[displayUser.status] ||
                        statusStyles.PENDING),
                      fontWeight: 600,
                    }}
                  />
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Contact Information Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.PRIMARY,
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Contact Information
              </Typography>

              {isEditMode ? (
                <>
                  {/* Email Input */}
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editData.email || ""}
                    onChange={(e) => handleEditChange("email", e.target.value)}
                    size="small"
                    sx={{ mb: 1.5 }}
                    disabled
                  />
                  {/* Phone Input */}
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editData.phone || ""}
                    onChange={(e) => {
                      const phoneValue = e.target.value.replace(/\D/g, "");
                      if (phoneValue.length <= 10) {
                        handleEditChange("phone", phoneValue);
                      }
                    }}
                    inputProps={{ inputMode: "numeric" }}
                    placeholder="10 digit phone number"
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                </>
              ) : (
                <>
                  {/* Email */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      mb: 1.5,
                      alignItems: "flex-start",
                    }}
                  >
                    <Icon
                      name="email"
                      color={COLORS.PRIMARY}
                      sx={{ mt: 0.5 }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.TEXT,
                          wordBreak: "break-word",
                        }}
                      >
                        {displayUser.email || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Phone */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                    }}
                  >
                    <Icon
                      name="phone"
                      color={COLORS.PRIMARY}
                      sx={{ mt: 0.5 }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
                      >
                        {displayUser.phone || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Role-Specific Details */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.PRIMARY,
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Details
              </Typography>

              {isEditMode ? (
                <>
                  {displayUser.role === "CLIENT" && (
                    <>
                      <TextField
                        fullWidth
                        label="User Role"
                        value="Client"
                        disabled
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <TextField
                        fullWidth
                        label="Client Role Type"
                        value={
                          displayUser.clientRole?.roleType ||
                          displayUser.roleType ||
                          displayUser.clientRoleType ||
                          (displayUser.companyName ? "Company" : "Individual")
                        }
                        disabled
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={editData.companyName || ""}
                        onChange={(e) =>
                          handleEditChange("companyName", e.target.value)
                        }
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <TextField
                        fullWidth
                        label="Company Address"
                        value={editData.companyAddress || ""}
                        onChange={(e) =>
                          handleEditChange("companyAddress", e.target.value)
                        }
                        multiline
                        rows={2}
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                    </>
                  )}
                  {(displayUser.role === "ENGINEER" || displayUser.role === "ADMIN" || displayUser.role === "SUPERADMIN") && (
                    <>
                      <TextField
                        fullWidth
                        label="Address"
                        value={editData.detailedAddress || ""}
                        onChange={(e) =>
                          handleEditChange("detailedAddress", e.target.value)
                        }
                        multiline
                        rows={2}
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <TextField
                        fullWidth
                        label="City"
                        value={editData.city || ""}
                        onChange={(e) =>
                          handleEditChange("city", e.target.value)
                        }
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <TextField
                        fullWidth
                        label="State"
                        value={editData.state || ""}
                        onChange={(e) =>
                          handleEditChange("state", e.target.value)
                        }
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Client Role & Role Type (For CLIENT) */}
                  {displayUser.role === "CLIENT" && (
                    <>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_SMALL,
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          User Role
                        </Typography>
                        <Chip
                          label="Client"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            mt: 0.5,
                            backgroundColor: "rgba(66, 26, 110, 0.12)",
                            color: COLORS.PRIMARY,
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_SMALL,
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          Client Role Type
                        </Typography>
                        <Chip
                          label={
                            displayUser.clientRole?.roleType ||
                            displayUser.roleType ||
                            displayUser.clientRoleType ||
                            (displayUser.companyName ? "Company" : "Individual")
                          }
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            mt: 0.5,
                            backgroundColor: (displayUser.clientRole?.roleType || displayUser.roleType || "").toLowerCase() === "individual"
                              ? "rgba(66, 26, 110, 0.12)"
                              : "rgba(33, 150, 243, 0.12)",
                            color: (displayUser.clientRole?.roleType || displayUser.roleType || "").toLowerCase() === "individual"
                              ? COLORS.PRIMARY
                              : "#1976D2",
                          }}
                        />
                      </Box>
                    </>
                  )}

                  {/* Company Name (For CLIENT) */}
                  {displayUser.role === "CLIENT" && displayUser.companyName && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        Company Name
                      </Typography>
                      <Typography
                        sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
                      >
                        {displayUser.companyName}
                      </Typography>
                    </Box>
                  )}

                  {/* Company Address (For CLIENT) */}
                  {displayUser.role === "CLIENT" &&
                    displayUser.companyAddress && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_SMALL,
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          Company Address
                        </Typography>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            color: COLORS.TEXT,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayUser.companyAddress}
                        </Typography>
                      </Box>
                    )}

                  {/* Detailed Address (For ENGINEER, ADMIN, SUPERADMIN) */}
                  {(displayUser.role === "ENGINEER" || displayUser.role === "ADMIN" || displayUser.role === "SUPERADMIN") &&
                    displayUser.detailedAddress && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_SMALL,
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          Address
                        </Typography>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            color: COLORS.TEXT,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayUser.detailedAddress}
                        </Typography>
                      </Box>
                    )}

                  {/* City (For ENGINEER, ADMIN, SUPERADMIN) */}
                  {(displayUser.role === "ENGINEER" || displayUser.role === "ADMIN" || displayUser.role === "SUPERADMIN") && displayUser.city && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        City
                      </Typography>
                      <Typography
                        sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
                      >
                        {displayUser.city}
                      </Typography>
                    </Box>
                  )}

                  {/* State (For ENGINEER, ADMIN, SUPERADMIN) */}
                  {(displayUser.role === "ENGINEER" || displayUser.role === "ADMIN" || displayUser.role === "SUPERADMIN") && displayUser.state && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        State
                      </Typography>
                      <Typography
                        sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}
                      >
                        {displayUser.state}
                      </Typography>
                    </Box>
                  )}

                  {/* Credits (For CLIENT) */}
                  {/* {displayUser.role === "CLIENT" && displayUser.credits && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_SMALL,
                          color: COLORS.MUTED_TEXT,
                        }}
                      >
                        Credits
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                        <Box>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.MUTED_TEXT,
                            }}
                          >
                            Total
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.PRIMARY,
                              fontWeight: 600,
                            }}
                          >
                            {displayUser.credits.totalCredits || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.MUTED_TEXT,
                            }}
                          >
                            Used
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.ERROR,
                              fontWeight: 600,
                            }}
                          >
                            {displayUser.credits.usedCredits || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.MUTED_TEXT,
                            }}
                          >
                            Remaining
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.SECONDARY,
                              fontWeight: 600,
                            }}
                          >
                            {(displayUser.credits.totalCredits || 0) -
                              (displayUser.credits.usedCredits || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )} */}
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Additional Information */}
            {!isEditMode && (
              <Box>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    mb: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Additional Info
                </Typography>

                {/* Status */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}
                  >
                    Account Status
                  </Typography>
                  <Chip
                    label={displayStatus}
                    size="small"
                    sx={{
                      ...(statusStyles[displayUser.status] ||
                        statusStyles.PENDING),
                      mt: 0.5,
                    }}
                  />
                </Box>

                {/* Joined Date */}
                <Box>
                  <Typography
                    sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}
                  >
                    Member Since
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.TEXT,
                      mt: 0.5,
                    }}
                  >
                    {joinedDate}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Action Buttons (Edit Mode) */}
            {isEditMode && (
              <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSaveChanges}
                  disabled={updating}
                >
                  {updating ? <CircularProgress size={24} /> : "Save"}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default UserProfileSidebar;
